import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import ModuleDetail from "../common/ModuleDetail";
import DataApi from "../../api/dataApi";
import { convertToUserTimezone } from "../../utils/convertTimeZone";
import { useTimeZone } from "../../contexts/TimeZoneContext";
import { Badge } from "react-bootstrap";

const ShelfDetail = ({ permissions }) => {

    const { timeZone } = useTimeZone();

    const other = permissions?.allowEdit
        ? {
            other: [
                { key: "createdbyid", label: "Created By", type: "text" },
                {
                    key: "createddate",
                    label: "Created Date",
                    type: "date",
                    render: (value) => convertToUserTimezone(value, timeZone),
                },
                {
                    key: "lastmodifieddate",
                    label: "Last Modified Date",
                    type: "date",
                    render: (value) => convertToUserTimezone(value, timeZone),
                },
                { key: "lastmodifiedbyid", label: "Last Modified By", type: "text" },
            ],
        }
        : {};

    const { id } = useParams();
    const [shelf, setShelf] = useState(null);

    const fetchShelfData = async (shelfId) => {
        try {
            const api = new DataApi("shelf");
            const res = await api.fetchById(shelfId);
            setShelf(res?.data || {});
        } catch (error) {
            console.error("Error fetching shelf:", error);
        }
    };

    useEffect(() => {
        if (id) fetchShelfData(id);
    }, [id]);

    const fields = {
        title: "name",
        subtitle: "floor",

        details: [
            {
                key: "name",
                label: "Category Name",
                type: "select",
                asyncSelect: true,
                required: true,
                displayKey: "name",
                placeholder: "Search or create category",
                helpText: "Search existing categories or type new name to create. Supports DDC (000-099) and LLC (AC-AZ) classifications.",
                colSize: 6,
                loadOptions: async (inputValue) => {
                    try {
                        const api = (await import('../../api/dataApi')).default;
                        const classificationApi = new api('classification');
                        const response = await classificationApi.get('/');
                        const classifications = response.data || [];

                        // Filter by input value and library classification setting
                        const filtered = classifications.filter(item => {
                            if (!inputValue) return true;

                            const searchValue = inputValue.toLowerCase().trim();

                            // Direct name/category matching
                            if (item.category?.toLowerCase().includes(searchValue) ||
                                item.name?.toLowerCase().includes(searchValue)) {
                                return true;
                            }

                            // Code matching (exact or partial)
                            if (item.code?.toLowerCase().includes(searchValue)) {
                                return true;
                            }

                            // Range matching
                            if (item.classification_from && item.classification_to) {
                                const isItemNumeric = /^\d+$/.test(item.classification_from) && /^\d+$/.test(item.classification_to);
                                const isSearchNumeric = /^\d+$/.test(searchValue);
                                const isItemAlpha = /^[a-zA-Z]+$/.test(item.classification_from) && /^[a-zA-Z]+$/.test(item.classification_to);
                                const isSearchAlpha = /^[a-zA-Z]+$/.test(searchValue);

                                // For numeric ranges (DDC)
                                if (isItemNumeric && isSearchNumeric) {
                                    const searchNum = parseInt(searchValue);
                                    if (searchNum >= parseInt(item.classification_from) && searchNum <= parseInt(item.classification_to)) {
                                        return true;
                                    }
                                }
                                // For alphabetic ranges (LLC)
                                else if (isItemAlpha && isSearchAlpha) {
                                    const searchAlpha = searchValue.toUpperCase();
                                    if (searchAlpha >= item.classification_from.toUpperCase() && searchAlpha <= item.classification_to.toUpperCase()) {
                                        return true;
                                    }
                                }
                            }
                            return false;
                        });

                        // Create options with detailed information
                        return filtered.map(item => ({
                            value: item.name,
                            label: `${item.category} - ${item.name} (${item.code}) [${item.classification_from || '0'} to ${item.classification_to || '0'}]`,
                            data: item
                        }));
                    } catch (e) {
                        console.error("Error loading classifications:", e);
                        return [];
                    }
                },
                defaultOptions: true,
                clearable: true,
                creatable: true,
                // MATCHED WITH FormModal.js signature: (fieldName, val, formData, setFormData, originalOption)
                onChange: (fieldName, val, formData, setFormData, originalOption) => {
                    if (typeof setFormData !== 'function') return;

                    if (originalOption && originalOption.data) {
                        const selected = originalOption.data;
                        setFormData(prev => ({
                            ...prev,
                            name: val,
                            classification_from: selected.classification_from || '',
                            classification_to: selected.classification_to || '',
                            classification_type: selected.classification_type || ''
                        }));
                    } else {
                        setFormData(prev => ({
                            ...prev,
                            name: val || '',
                            classification_from: '',
                            classification_to: '',
                            classification_type: ''
                        }));
                    }
                }
            },
            { key: "floor", label: "Floor", type: "text", required: true },
            { key: "rack", label: "Rack", type: "text", required: true },
            { key: "shelf", label: "Shelf", type: "text", required: true },
            {
                key: "classification_from",
                label: "Range From",
                type: "text",
                required: false,
                placeholder: "Auto-filled",
                colSize: 6,
                readOnly: true,
                helpText: "Auto-filled from category selection"
            },
            {
                key: "classification_to",
                label: "Range To",
                type: "text",
                required: false,
                placeholder: "Auto-filled",
                colSize: 6,
                readOnly: true,
                helpText: "Auto-filled from category selection"
            },
            {
                key: "capacity",
                label: "Capacity",
                type: "text",
                render: (value) => <Badge bg="secondary">{value || 100}</Badge>
            },
            { key: "is_active", label: "Status", type: "toggle" },
        ],
        ...other
    };

    return (
        <>
            {shelf && (
                <ModuleDetail
                    moduleName="shelf"
                    moduleApi="shelf"
                    moduleLabel="Rack Mapping"
                    icon="fa-solid fa-layer-group"
                    fields={fields}
                    data={shelf}
                    permissions={permissions || {}}
                />
            )}
        </>
    );
};

export default ShelfDetail;